import ProgressIndicator from "components/ProgressIndicator";
import { FeaturesContext } from "contexts/FeaturesContext";
import { EntitlementContext } from "contexts/UserProfileContext";
import React, { useContext } from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import { AlertRoutes } from "routes/AlertRoutes";
import { CaseManagementRoutes } from "routes/CaseManagementRoutes";
import { DataExplorationRoutesV2 } from "routes/DataExplorationRoutesV2";
import { DownDerateRoutes } from "routes/DownDerateRoutes";
import { FleetManagerRoutes } from "routes/FleetManagerRoutes";
import { FleetRoutes } from "routes/FleetRoutes";
import { LostEnergyRoutes } from "routes/LostEnergyRoutes";
import { MachineRoutes } from "routes/MachineRoutes";
import { MyActionsRoute } from "routes/MyActionsRoute";
import { ProjectsRoutes } from "routes/ProjectsRoutes";
import { KpisRoutes } from "routes/KpisRoutes";
import { SettingsRoutes } from "routes/SettingsRoutes";
import { SiteRoutes } from "routes/SiteRoutes";

import { SysInfoRoutes } from "./SysInfoRoutes";
import { UserManagementRoutes } from "./UserManagementRoutes";

export const PageRoutes = (): JSX.Element => {
  const {
    hasEntitlement,
    hasEntitlements,
    loading: entitlementsLoading,
    error: entitlementsError
  } = useContext(EntitlementContext);

  const {
    hasFeatures,
    loading: featuresLoading,
    error: featuresError
  } = useContext(FeaturesContext);

  const loading = entitlementsLoading || featuresLoading;
  const error = entitlementsError || featuresError;

  if (loading || error) {
    return <ProgressIndicator loading={loading} error={error} />;
  }

  return (
    <Switch>
      {hasEntitlements(["overviews-view"]) && hasFeatures(["overviews"]) && (
        <Route path={"/pages/fleet"} component={FleetRoutes} />
      )}

      {hasEntitlements(["overviews-view"]) && hasFeatures(["overviews"]) && (
        <Route path={"/pages/site"} component={SiteRoutes} />
      )}

      {hasEntitlements(["overviews-view"]) && hasFeatures(["overviews"]) && (
        <Route path={"/pages/machine"} component={MachineRoutes} />
      )}

      {hasEntitlements(["cm-my-actions-view"]) &&
        hasFeatures(["my_actions", "case_management"]) && (
          <Route path={"/pages/my-actions"} component={MyActionsRoute} />
        )}

      {hasEntitlements(["cm-view", "cm-edit"]) &&
        hasFeatures(["case_management"]) && (
          <Route
            path={"/pages/case-management"}
            component={CaseManagementRoutes}
          />
        )}

      {hasEntitlements(["projects-view", "cm-view"]) &&
        hasFeatures(["projects", "case_management"]) && (
          <Route path={"/pages/projects"} component={ProjectsRoutes} />
        )}

      {hasEntitlements(["kpis-view"]) && hasFeatures(["kpis"]) && (
        <Route path={"/pages/kpis"} component={KpisRoutes} />
      )}

      {hasEntitlements(["alerts-view", "alerts-edit"]) &&
        hasFeatures(["alerts"]) && (
          <Route path={"/pages/alerts"} component={AlertRoutes} />
        )}

      {hasEntitlements(["lostenergydashboard-view"]) &&
        hasFeatures(["lost_energy_dashboard"]) && (
          <Route path={"/pages/lost-energy"} component={LostEnergyRoutes} />
        )}

      {hasEntitlements(["downderatedtracker-view", "cm-view"]) &&
        hasFeatures(["down_derated_tracker", "case_management"]) && (
          <Route
            path={"/pages/down-derate-tracker"}
            component={DownDerateRoutes}
          />
        )}

      {hasEntitlements(["powerbi-view"]) && hasFeatures(["powerbi"]) && (
        <Route path={"/pages/fleet-manager"} component={FleetManagerRoutes} />
      )}

      {hasEntitlements(["data-exploration-v2-view"]) &&
        hasFeatures(["data_exploration_v2"]) && (
          <Route
            path={"/pages/data-exploration"}
            component={DataExplorationRoutesV2}
          />
        )}

      {hasEntitlements(["onyxsettings-edit"]) && hasFeatures([]) && (
        <Route path={"/pages/settings"} component={SettingsRoutes} />
      )}

      {hasEntitlements(["onyxsettings-edit"]) && hasFeatures([]) && (
        <Route path={"/pages/sys-info"} component={SysInfoRoutes} />
      )}

      {[
        "onyxsettings-edit",
        "onyxsettings-view",
        "customer-user-management-edit",
        "customer-user-management-view"
      ].some(ent => hasEntitlement(ent)) &&
        hasFeatures([]) && (
          <Route path={"/pages/users"} component={UserManagementRoutes} />
        )}

      {hasEntitlements(["overviews-view"]) && hasFeatures([]) && (
        <Redirect from="/pages" to="/pages/fleet" />
      )}

      {/* Redirect routes */}

      {hasEntitlements(["overviews-view"]) && hasFeatures(["overviews"]) && (
        <Redirect from="/pages" to="/pages/fleet" />
      )}
      {hasEntitlements(["overviews-view"]) && hasFeatures([]) && (
        <Redirect from="/pages" to="/pages/site" />
      )}
      {hasEntitlements(["overviews-view"]) && hasFeatures([]) && (
        <Redirect from="/pages" to="/pages/machine" />
      )}
      {hasEntitlements(["cm-my-actions-view"]) &&
        hasFeatures(["my_actions"]) && (
          <Redirect from="/pages" to="/pages/my-actions" />
        )}
      {hasEntitlements(["cm-view", "cm-edit"]) &&
        hasFeatures(["case_management"]) && (
          <Redirect from="/pages" to="/pages/case-management" />
        )}
      {hasEntitlements(["alerts-view", "alerts-edit"]) &&
        hasFeatures(["alerts"]) && (
          <Redirect from="/pages" to="/pages/alerts" />
        )}
      {hasEntitlements(["lostenergydashboard-view"]) &&
        hasFeatures(["lost_energy_dashboard"]) && (
          <Redirect from="/pages" to="/pages/lost-energy" />
        )}
      {hasEntitlements(["downderatedtracker-view"]) &&
        hasFeatures(["down_derated_tracker", "case_management"]) && (
          <Redirect from="/pages" to="/pages/down-derate-tracker" />
        )}
      {hasEntitlements(["powerbi-view"]) && hasFeatures(["powerbi"]) && (
        <Redirect from="/pages" to="/pages/fleet-manager" />
      )}
      {hasEntitlements(["data-exploration-v2-view"]) &&
        hasFeatures(["data_exploration_v2"]) && (
          <Redirect from="/pages" to="/pages/data-exploration" />
        )}
      {hasEntitlements(["onyxsettings-edit"]) && hasFeatures([]) && (
        <Redirect from="/pages" to="/pages/settings" />
      )}
      {hasEntitlements(["onyxsettings-edit"]) && hasFeatures([]) && (
        <Redirect from="/pages" to="/pages/sys-info" />
      )}
      {hasEntitlements([
        "onyxsettings-edit",
        "customer-user-management-edit",
        "customer-user-management-view"
      ]) &&
        hasFeatures([]) && <Redirect from="/pages" to="/pages/users" />}

      <Redirect to="/no-access" />
    </Switch>
  );
};
